import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Header } from '../components/layout/Header';
import { useToast } from '../components/toast/Toast';
import { StatusPill } from '../components/ui/StatusPill';
import { usersApi, type User, type CreateUserBody } from '../api/modules';
import { relativeTime } from '../lib/format';

const TIERS = ['any', 'T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'] as const;
const STATUSES = ['any', 'active', 'suspended', 'pending'] as const;
const SCOPE_TYPES: CreateUserBody['scope_type'][] = ['state', 'mandal', 'district', 'block', 'gram_panchayat', 'nyaya_panchayat', 'self'];

function CreateUserModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { show } = useToast();
  const qc = useQueryClient();
  const [form, setForm] = useState<CreateUserBody>({
    username: '', full_name: '', designation: '', role_code: 'R13', scope_type: 'state',
  });
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const roles = useQuery({ enabled: open, queryKey: ['users-roles'], queryFn: usersApi.roles });

  const create = useMutation({
    mutationFn: () => usersApi.create({
      ...form,
      // Coerce empties to undefined
      email: form.email || undefined,
      mobile: form.mobile || undefined,
      designation: form.designation || undefined,
      scope_id: form.scope_id || undefined,
    }),
    onSuccess: (d) => {
      show(`User created · ${d.username}`);
      setTempPassword(d.temporary_password);
      void qc.invalidateQueries({ queryKey: ['users-list'] });
    },
    onError: (err) => show(`Create failed: ${(err as Error).message}`),
  });

  if (!open) return null;
  const scopeRequiresId = form.scope_type !== 'state' && form.scope_type !== 'self';

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-cream-200 flex items-center justify-between">
          <h2 className="serif-display text-xl text-navy-800 font-medium">Create user</h2>
          <button type="button" onClick={onClose} className="text-navy-800/60 hover:text-navy-800 text-xl">×</button>
        </div>

        {tempPassword ? (
          <div className="p-6 space-y-4">
            <div className="bg-green-50 border border-green-300 p-4 rounded-sm">
              <div className="text-sm font-medium text-green-900 mb-2">User created successfully</div>
              <div className="text-xs text-green-800 mb-3">Share this temporary password with the user securely. They must change it on first login.</div>
              <div className="bg-white border border-green-200 rounded-sm p-3 font-numeric text-base text-navy-800 select-all">
                {tempPassword}
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => { setTempPassword(null); setForm({ username: '', full_name: '', designation: '', role_code: 'R13', scope_type: 'state' }); onClose(); }}
                className="px-4 py-2 bg-navy-800 hover:bg-navy-700 text-white text-sm rounded-sm"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 grid grid-cols-2 gap-4 text-sm">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-navy-800/60 font-semibold">Username *</label>
              <input
                type="text"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder="lowercase.dotted"
                className="mt-1 w-full px-3 py-2 border border-cream-300 rounded-sm focus:outline-none focus:border-saffron-500 font-numeric text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-navy-800/60 font-semibold">Full name *</label>
              <input
                type="text"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-cream-300 rounded-sm focus:outline-none focus:border-saffron-500 text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-navy-800/60 font-semibold">Email</label>
              <input
                type="email"
                value={form.email ?? ''}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-cream-300 rounded-sm focus:outline-none focus:border-saffron-500 text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-navy-800/60 font-semibold">Mobile</label>
              <input
                type="tel"
                value={form.mobile ?? ''}
                onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                placeholder="+91XXXXXXXXXX"
                className="mt-1 w-full px-3 py-2 border border-cream-300 rounded-sm focus:outline-none focus:border-saffron-500 font-numeric text-sm"
              />
            </div>
            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-widest text-navy-800/60 font-semibold">Designation</label>
              <input
                type="text"
                value={form.designation ?? ''}
                onChange={(e) => setForm({ ...form, designation: e.target.value })}
                placeholder="e.g. Chief Veterinary Officer"
                className="mt-1 w-full px-3 py-2 border border-cream-300 rounded-sm focus:outline-none focus:border-saffron-500 text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-navy-800/60 font-semibold">Role *</label>
              <select
                value={form.role_code}
                onChange={(e) => setForm({ ...form, role_code: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-cream-300 rounded-sm focus:outline-none focus:border-saffron-500 text-sm bg-white"
              >
                {roles.data?.map((r) => (
                  <option key={r.code} value={r.code}>{r.code} — {r.name} ({r.tier})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-navy-800/60 font-semibold">Scope</label>
              <select
                value={form.scope_type}
                onChange={(e) => setForm({ ...form, scope_type: e.target.value as CreateUserBody['scope_type'], scope_id: undefined })}
                className="mt-1 w-full px-3 py-2 border border-cream-300 rounded-sm focus:outline-none focus:border-saffron-500 text-sm bg-white capitalize"
              >
                {SCOPE_TYPES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </select>
            </div>
            {scopeRequiresId && (
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-navy-800/60 font-semibold">Scope ID *</label>
                <input
                  type="number"
                  value={form.scope_id ?? ''}
                  onChange={(e) => setForm({ ...form, scope_id: parseInt(e.target.value, 10) || undefined })}
                  placeholder="e.g. mandal/district/GP id"
                  className="mt-1 w-full px-3 py-2 border border-cream-300 rounded-sm focus:outline-none focus:border-saffron-500 font-numeric text-sm"
                />
                <div className="text-[11px] text-navy-800/50 mt-1">Required for non-state/self scopes</div>
              </div>
            )}
            <div className="col-span-2 flex items-center justify-end gap-2 pt-2 border-t border-cream-100">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-cream-300 text-navy-800/80 hover:bg-cream-100 text-sm rounded-sm">
                Cancel
              </button>
              <button
                type="button"
                disabled={create.isPending || !form.username || !form.full_name || (scopeRequiresId && !form.scope_id)}
                onClick={() => create.mutate()}
                className="px-4 py-2 bg-saffron-500 hover:bg-saffron-600 disabled:opacity-50 text-white text-sm rounded-sm"
              >
                {create.isPending ? 'Creating…' : 'Create user'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function UsersPage() {
  const { show } = useToast();
  const qc = useQueryClient();
  const [tier, setTier] = useState<(typeof TIERS)[number]>('any');
  const [status, setStatus] = useState<(typeof STATUSES)[number]>('any');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const list = useQuery({
    queryKey: ['users-list', { tier, status, search }],
    queryFn: () => usersApi.list({
      tier: tier === 'any' ? undefined : tier,
      status: status === 'any' ? undefined : status,
      search: search || undefined,
    }),
  });

  const setUserStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'suspended' }) => usersApi.setStatus(id, status),
    onSuccess: (_d, vars) => {
      show(`User ${vars.status === 'suspended' ? 'suspended' : 'activated'}`);
      void qc.invalidateQueries({ queryKey: ['users-list'] });
    },
    onError: (err) => show(`Failed: ${(err as Error).message}`),
  });

  const resetPw = useMutation({
    mutationFn: (id: string) => usersApi.resetPassword(id),
    onSuccess: (d) => show(`New temp password: ${d.temporary_password.slice(0, 6)}…`),
    onError: (err) => show(`Failed: ${(err as Error).message}`),
  });

  const users: User[] = list.data ?? [];
  const total = users.length;
  const active = users.filter((u) => u.status === 'active').length;
  const suspended = users.filter((u) => u.status === 'suspended').length;
  const recentLogins = users.filter((u) => u.last_login_at && (Date.now() - new Date(u.last_login_at).getTime()) < 24 * 3600 * 1000).length;

  return (
    <>
      <Header breadcrumbs={['Tools', 'User Management']} />
      <div className="px-8 pt-7 pb-2">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-navy-800/50 mb-1">Tools</div>
            <h1 className="serif-display text-3xl text-navy-800 font-medium">User Management</h1>
            <p className="text-sm text-navy-800/60 mt-1">
              18 roles × 7 tiers · live from /api/users · creating, suspending and password reset are audit-logged
            </p>
          </div>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="text-sm px-3 py-2 bg-saffron-500 hover:bg-saffron-600 text-white rounded-sm transition flex items-center gap-2"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM20 8v6M23 11h-6" />
            </svg>
            New user
          </button>
        </div>
      </div>

      <div className="px-8 py-5 space-y-5 overflow-y-auto">
        {/* Metric cards */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'Total users',      value: total },
            { label: 'Active',           value: active,        sub: `${total === 0 ? 0 : Math.round(active/total*100)}% of total` },
            { label: 'Suspended',        value: suspended,     sub: 'review pending' },
            { label: 'Logged in (24 h)', value: recentLogins,  sub: 'currently active' },
          ].map((m) => (
            <div key={m.label} className="bg-white border border-cream-200 p-4 rounded-sm">
              <div className="text-[10px] uppercase tracking-widest text-navy-800/50">{m.label}</div>
              <div className="font-numeric serif-display text-2xl text-navy-800 font-medium mt-1">{m.value}</div>
              {m.sub && <div className="text-[11px] text-navy-800/60 mt-1.5 font-numeric">{m.sub}</div>}
            </div>
          ))}
        </div>

        {/* Filter chips */}
        <div className="bg-white border border-cream-200 rounded-sm p-4 flex items-center gap-3 flex-wrap text-xs">
          <span className="text-navy-800/60">Tier:</span>
          {TIERS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTier(t)}
              className={`px-3 py-1.5 rounded-sm transition ${tier === t ? 'bg-navy-800 text-white' : 'bg-cream-100 text-navy-800/80 hover:bg-cream-200'}`}
            >
              {t === 'any' ? 'All' : t}
            </button>
          ))}
          <span className="text-cream-300 mx-1">|</span>
          <span className="text-navy-800/60">Status:</span>
          {STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              className={`px-3 py-1.5 rounded-sm transition ${status === s ? 'bg-navy-800 text-white' : 'bg-cream-100 text-navy-800/80 hover:bg-cream-200'}`}
            >
              {s === 'any' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
          <input
            type="text"
            placeholder="Search by name or username…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="ml-auto text-xs px-3 py-1.5 border border-cream-300 rounded-sm bg-white w-64 focus:outline-none focus:border-saffron-500"
          />
        </div>

        {/* Table */}
        <div className="bg-white border border-cream-200 rounded-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-cream-100 text-xs text-navy-800/60">
            {list.isLoading ? 'Loading…' : `${users.length} users`}
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-cream-50">
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Username</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Name</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Role</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Tier · Scope</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Status</th>
                <th className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Last login</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {list.isLoading || !list.data ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="border-t border-cream-100"><td colSpan={7} className="px-3 py-3"><div className="h-3 bg-cream-100 rounded animate-pulse" /></td></tr>
                ))
              ) : users.length === 0 ? (
                <tr><td colSpan={7} className="px-5 py-8 text-center text-sm text-navy-800/50">No users match the current filters</td></tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id} className="border-t border-cream-100 hover:bg-cream-50">
                    <td className="px-3 py-2.5 font-numeric text-navy-800/80 text-xs">{u.username}</td>
                    <td className="px-3 py-2.5 font-medium text-navy-800">{u.full_name}</td>
                    <td className="px-3 py-2.5 text-navy-800/80 text-xs">{u.role ? `${u.role.code} — ${u.role.name}` : <span className="text-amber-600">No role</span>}</td>
                    <td className="px-3 py-2.5 text-navy-800/70 text-xs">
                      {u.role && <span className="pill pill-navy mr-1.5">{u.role.tier}</span>}
                      <span className="capitalize">{u.scope?.type.replace('_', ' ') ?? '—'}</span>
                      {u.scope?.id !== null && u.scope?.id !== undefined && <span className="ml-1 text-navy-800/50 font-numeric">#{u.scope.id}</span>}
                    </td>
                    <td className="px-3 py-2.5"><StatusPill status={u.status} /></td>
                    <td className="px-3 py-2.5 font-numeric text-navy-800/70 text-xs">{u.last_login_at ? relativeTime(u.last_login_at) : 'Never'}</td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          type="button"
                          onClick={() => resetPw.mutate(u.id)}
                          disabled={resetPw.isPending}
                          className="text-xs text-navy-800/60 hover:text-saffron-600 font-medium"
                        >
                          Reset PW
                        </button>
                        <span className="text-navy-800/20">|</span>
                        {u.status === 'suspended' ? (
                          <button
                            type="button"
                            onClick={() => setUserStatus.mutate({ id: u.id, status: 'active' })}
                            className="text-xs text-green-700 hover:text-green-800 font-medium"
                          >
                            Activate
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setUserStatus.mutate({ id: u.id, status: 'suspended' })}
                            className="text-xs text-red-600 hover:text-red-700 font-medium"
                          >
                            Suspend
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CreateUserModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </>
  );
}
